import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { t, translations } from '../data/translations';

// Feature card component - Opti.ai exact style with fast hover
const FeatureCard = ({ title, description, isHighlighted, illustration, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    whileHover={{ y: -5 }}
    className={`rounded-3xl p-10 h-full flex flex-col cursor-pointer shadow-lg hover:shadow-xl overflow-hidden ${
      isHighlighted 
        ? 'bg-[#E8A238] hover:bg-[#D97706]' 
        : 'bg-[#FDF8F0] hover:bg-[#E8A238]'
    }`}
    style={{
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      transition: 'background-color 0.15s ease-out, transform 0.2s ease-out, box-shadow 0.2s ease-out'
    }}
  >
    {/* Illustration */}
    <div className="h-44 mb-6 flex items-center justify-start">
      <svg viewBox="0 0 100 100" className="w-28 h-28">
        {illustration}
      </svg>
    </div>
    
    {/* Content */}
    <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight text-[#131132] break-words hyphens-auto">
      {title}
    </h3>
    <p className="text-lg leading-relaxed text-[#131132]/60">
      {description}
    </p>
  </motion.div>
);

const AdvantageSection = () => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();
  const positions = translations.positionsSection.list[language] || translations.positionsSection.list.sk;

  // Spiral illustration
  const spiralIllustration = (
    <g stroke="#131132" strokeWidth="1.5" fill="none">
      <path d="M50 10 C 85 10, 95 45, 95 55 C 95 80, 70 95, 50 95 C 25 95, 5 75, 5 50 C 5 30, 25 15, 50 15 C 70 15, 85 30, 85 50 C 85 68, 68 82, 50 82 C 35 82, 22 68, 22 50 C 22 38, 35 28, 50 28 C 62 28, 72 38, 72 50 C 72 60, 62 68, 50 68" />
      <circle cx="68" cy="38" r="4" fill="#131132" />
    </g>
  );

  // Grid illustration
  const gridIllustration = (
    <g stroke="#131132" strokeWidth="1.5" fill="none">
      {[0, 1, 2].map(row =>
        [0, 1, 2].map(col => (
          <circle
            key={`${row}-${col}`}
            cx={28 + col * 22}
            cy={28 + row * 22}
            r="7"
            fill={row === 0 && col === 0 ? '#131132' : 'none'}
          />
        ))
      )}
    </g>
  );

  // Orbit illustration
  const orbitIllustration = (
    <g stroke="#131132" strokeWidth="1.5" fill="none">
      <ellipse cx="50" cy="50" rx="40" ry="20" />
      <circle cx="80" cy="38" r="5" fill="#131132" />
    </g>
  );

  const features = [
    {
      title: t('whyWorkPro.card1Title', language),
      description: t('whyWorkPro.card1Desc', language),
      illustration: spiralIllustration,
      isHighlighted: false,
    },
    {
      title: t('whyWorkPro.card2Title', language),
      description: t('whyWorkPro.card2Desc', language),
      illustration: gridIllustration,
      isHighlighted: false,
    },
    {
      title: t('whyWorkPro.card3Title', language),
      description: t('whyWorkPro.card3Desc', language),
      illustration: orbitIllustration,
      isHighlighted: true,
    },
  ];

  return (
    <>
      {/* Why WorkPro */}
      <section className="bg-[#FDF8F0] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#131132]">
              {t('whyWorkPro.title', language)}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard 
                key={feature.title} 
                {...feature} 
                delay={index * 0.15}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Positions */}
      <section className="bg-[#131132] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('positionsSection.title', language)}
            </h2>
            <p className="text-xl text-white/50 max-w-2xl mx-auto">
              {t('positionsSection.subtitle', language)}
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {positions.map((position, index) => (
              <motion.div
                key={position}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                whileHover={{ scale: 1.05, y: -3 }}
                className="flex items-center gap-2 px-5 py-3 bg-white/10 rounded-full cursor-pointer hover:bg-[#E8A238] transition-all group"
              >
                <CheckCircle2 className="w-5 h-5 text-[#E8A238] group-hover:text-white transition-colors" />
                <span className="text-white font-medium">{position}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-center mt-12"
          >
            <motion.button
              onClick={openContactModal}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#E8A238] text-[#131132] font-semibold text-lg rounded-full hover:bg-[#D97706] transition-colors"
              data-testid="positions-contact-button"
            >
              {t('positionsSection.cta', language)}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#FDF8F0] py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#131132] mb-6">
              {t('howItWorks.title', language)}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: t('howItWorks.step1Title', language), desc: t('howItWorks.step1Desc', language) },
              { step: '02', title: t('howItWorks.step2Title', language), desc: t('howItWorks.step2Desc', language) },
              { step: '03', title: t('howItWorks.step3Title', language), desc: t('howItWorks.step3Desc', language) },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center"
              >
                <div className="text-8xl font-bold text-[#E8A238]/20 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold text-[#131132] mb-3">{item.title}</h3>
                <p className="text-[#131132]/60 text-lg">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AdvantageSection;
